import { ReactElement, useState } from 'react';
import BatchSaveIcon from '@mui/icons-material/ChecklistTwoTone';
import SaveIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { batchLegLabel, EditableCellForwardedProps, EditableContainerProps } from './Dispatch/types';
import { EditSaveScope } from './types';
import classNames from 'classnames/bind';

import styles from './EditableContainer.module.css';

const cx = classNames.bind(styles);

/**
 * Container Component that holds the Editable Cell inputs. This component provides a header bar with a Close icon.
 * The onClose prop is forwarded on to the rendering children components to allow them to close the popover if needed.
 */
export default function EditableContainer({ editableRef, selectedRowCount = 0, ...props }: EditableContainerProps) {
  const { onClose } = props;
  const [isValid, setIsValid] = useState(true);

  function save(scope: EditSaveScope) {
    if (!isValid) {
      return;
    }
    editableRef.current?.saveChanges(scope);
    onClose?.();
  }

  function handleKeyDown(ev: React.KeyboardEvent) {
    if (ev.key === 'Enter') {
      save('row');
    }
  }

  return (
    <div
      className={styles['editable-cell']}
      onKeyDown={handleKeyDown}>
      <div className={styles['editable-header']}>
        <span>{batchLegLabel('Edit Cell', selectedRowCount)}</span>
        <div className={styles['editable-header-actions']}>
          {selectedRowCount > 1 && (
            <BatchSaveIcon
              className={cx('close-ico-btn', { 'close-ico-btn-disabled': !isValid })}
              color='info'
              titleAccess='Save Changes to All Selected Rows'
              onClick={() => {
                save('selection');
              }}
            />
          )}
          <SaveIcon
            className={cx('close-ico-btn', { 'close-ico-btn-disabled': !isValid })}
            color='success'
            titleAccess='Save Changes'
            onClick={() => {
              save('row');
            }}
          />
          <CloseIcon
            className={styles['close-ico-btn']}
            color='error'
            titleAccess='Cancel Changes'
            onClick={() => {
              if (props.onClose) {
                props.onClose();
              }
            }}
          />
        </div>
      </div>
      <div className={styles['editable-content']}>
        {React.Children.map(props.children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child as ReactElement<EditableCellForwardedProps>, {
              onClose: props.onClose,
              onValidityChange: setIsValid
            });
          }
          return child;
        })}
      </div>
    </div>
  );
}
